// Textos e sugestões do assistente de criação (portado da POC).
import { Globe, MessageCircle, Phone, Store, UserPlus, type LucideIcon } from 'lucide-react'
import type { Goal } from '@impulso/shared'
import { defaultNationwide, type BlueprintDto } from '@impulso/shared/api/builder'

export const STEPS = ['Objetivo', 'Público', 'Anúncio', 'Orçamento', 'Revisão'] as const

export const GOALS: Array<{ id: Goal; icon: LucideIcon; title: string; desc: string }> = [
  { id: 'whatsapp', icon: MessageCircle, title: 'Receber mensagens no WhatsApp', desc: 'Pessoas clicam no anúncio e já caem numa conversa com você.' },
  { id: 'site', icon: Globe, title: 'Vender no meu site ou loja online', desc: 'Leva as pessoas direto para a página do produto.' },
  { id: 'leads', icon: UserPlus, title: 'Conseguir cadastros no site', desc: 'Teste grátis, pedido de orçamento ou de demonstração.' },
  { id: 'store', icon: Store, title: 'Trazer gente até a loja', desc: 'Aparece para quem está perto e mostra como chegar.' },
  { id: 'calls', icon: Phone, title: 'Receber ligações', desc: 'Um botão de ligar direto no anúncio do Google.' },
]

export const RESULT_WORD: Record<Goal, [string, string]> = {
  whatsapp: ['conversa no WhatsApp', 'conversas no WhatsApp'],
  site: ['visita com intenção de compra', 'visitas com intenção de compra'],
  leads: ['cadastro', 'cadastros'],
  store: ['pessoa interessada em ir à loja', 'pessoas interessadas em ir à loja'],
  calls: ['ligação', 'ligações'],
}

export const CTA_LABEL: Record<Goal, string> = { whatsapp: 'Enviar mensagem', site: 'Comprar agora', leads: 'Cadastre-se', store: 'Como chegar', calls: 'Ligar' }

const SEGMENTS: Array<{ match: RegExp; interests: string[]; keywords: string[] }> = [
  { match: /confeit|doce|bolo|padaria|caf/i, interests: ['Pais e mães', 'Festas infantis', 'Aniversários', 'Confeitaria', 'Casamentos', 'Chá de bebê', 'Doces gourmet', 'Eventos corporativos'], keywords: ['bolo de aniversário', 'confeitaria perto de mim', 'bolo personalizado', 'doces para festa', 'encomenda de bolo'] },
  { match: /sal[aã]o|beleza|cabel|est[eé]tica|unha|barbe/i, interests: ['Beleza', 'Cuidados com o cabelo', 'Maquiagem', 'Casamentos', 'Moda', 'Bem-estar'], keywords: ['salão de beleza perto de mim', 'corte de cabelo', 'manicure', 'escova progressiva', 'design de sobrancelha'] },
  { match: /restaur|lanch|pizz|hamb|comida|bar\b|delivery/i, interests: ['Gastronomia', 'Delivery de comida', 'Restaurantes', 'Happy hour', 'Comida caseira'], keywords: ['restaurante perto de mim', 'delivery de comida', 'almoço executivo', 'pizza delivery', 'hambúrguer artesanal'] },
  { match: /cl[ií]nic|sa[uú]de|odont|dent|fisio|psic|m[eé]dic/i, interests: ['Saúde e bem-estar', 'Pais e mães', 'Qualidade de vida', 'Exercícios físicos'], keywords: ['clínica perto de mim', 'consulta particular', 'dentista', 'fisioterapia', 'agendar consulta'] },
  { match: /software|saas|aplicativo|\bapp\b|sistema|tecnologia/i, interests: ['Pequenas empresas', 'Empreendedorismo', 'Tecnologia', 'Produtividade', 'Marketing digital', 'Gestão de negócios'], keywords: ['sistema de gestão', 'software para empresas', 'teste grátis', 'alternativa ao', 'melhor sistema para'] },
  { match: /curso|infoproduto|mentoria|educa/i, interests: ['Educação a distância', 'Desenvolvimento pessoal', 'Empreendedorismo', 'Carreira'], keywords: ['curso online', 'curso com certificado', 'aprender', 'mentoria', 'aula online'] },
  { match: /loja|moda|roupa|cal[cç]ad|acess[oó]r|presente/i, interests: ['Moda', 'Compras', 'Roupas femininas', 'Roupas masculinas', 'Presentes', 'Acessórios'], keywords: ['loja de roupas', 'moda feminina', 'presentes', 'roupas perto de mim', 'promoção de roupas'] },
]
const DEFAULT_SEGMENT = { interests: ['Compras', 'Comércio local', 'Família', 'Promoções', 'Serviços'], keywords: [] as string[] }

export function suggestionsFor(segment: string | null | undefined) {
  return SEGMENTS.find((s) => segment && s.match.test(segment)) ?? DEFAULT_SEGMENT
}

/** Blueprint inicial para um rascunho novo. */
export function initialBlueprint(opts: { goal: Goal | null; segment: string | null; city: string | null; offer?: string | null; headline?: string | null; body?: string | null; name?: string | null }): BlueprintDto {
  const sug = suggestionsFor(opts.segment)
  return {
    goal: opts.goal ?? 'whatsapp',
    name: opts.name?.slice(0, 120) || 'Nova campanha',
    offer: opts.offer ?? '',
    audience: { radiusKm: 8, nationwide: defaultNationwide({ ...opts, goal: opts.goal ?? 'whatsapp' }), ageMin: 25, ageMax: 55, interests: sug.interests.slice(0, 2), advantageAudience: true, keywords: sug.keywords.slice(0, 3) },
    creative: { headline: opts.headline ?? '', body: opts.body ?? '', imageAssetId: null, alternatives: [] },
    budget: { dailyBudget: 20, startDate: null, endDate: null, split: 'auto' },
    destination: { whatsappNumber: null, url: null, phone: null },
  }
}

/** Data local 'YYYY-MM-DD' daqui a n dias. */
export function isoDay(offsetDays = 0) {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function dayBR(iso: string) {
  const [y, m, d] = iso.split('-')
  return `${d}/${m}${y && y !== String(new Date().getFullYear()) ? `/${y}` : ''}`
}

export function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?'
}

export function handleFrom(name: string) {
  return name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '') || 'seunegocio'
}
